import { ApiError } from "@/lib/api";
import { type ErrorComponentProps, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";

/**
 * Error of a single page, shown in place of that page only: the sidebar and
 * whatever else the layout holds stay. A lost connection or a restarting
 * server retries by itself, so a deploy passes without anyone clicking.
 */

const CHUNK_ERROR =
  /dynamically imported module|importing a module script failed|error loading dynamically imported|unable to preload/i;
const NETWORK_ERROR = /failed to fetch|networkerror|load failed|network request failed|timed out/i;

export function isStaleBuildError(error: unknown): boolean {
  return error instanceof Error && CHUNK_ERROR.test(error.message);
}

function isTransient(error: unknown): boolean {
  if (error instanceof ApiError) return error.status >= 502 && error.status <= 504;
  return error instanceof TypeError && NETWORK_ERROR.test(error.message);
}

export function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const stale = isStaleBuildError(error);
  const transient = !stale && isTransient(error);
  const [retrying, setRetrying] = useState(false);

  const retry = async () => {
    setRetrying(true);
    try {
      reset();
      await router.invalidate();
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => {
    if (!transient) return;
    const id = setInterval(() => {
      reset();
      void router.invalidate().catch(() => {});
    }, 5_000);
    return () => clearInterval(id);
  }, [transient, reset, router]);

  return (
    <div className="flex justify-center py-16">
      <div className="max-w-md rounded-lg border bg-card p-8 text-center shadow-sm">
        <h1 className="font-heading text-xl font-bold">
          {stale ? "Hub was updated" : transient ? "Reconnecting…" : "This page failed to load"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {stale
            ? "A new version went out and this page needs a reload. Text you were typing is kept."
            : transient
              ? "Hub isn't answering right now (an update may be going out). This page comes back by itself."
              : error.message}
        </p>
        <button
          type="button"
          onClick={() => (stale ? window.location.reload() : void retry())}
          disabled={retrying}
          className="mt-4 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
        >
          {stale ? "Reload" : retrying ? "Retrying…" : "Retry"}
        </button>
      </div>
    </div>
  );
}
